/**
 * SEO Utilities for canonical URL management and domain consistency
 * Fixes Google Search Console "Duplicate without user-selected canonical page" issues
 */

/**
 * Get the canonical domain (always without www)
 * @returns {string} The canonical domain
 */
export const getCanonicalDomain = () => {
  return 'https://vinspectorai.com';
};

/**
 * Generate canonical URL for any page
 * @param {string} pathname - The pathname (e.g., '/demo-report', '/blog')
 * @param {Object} searchParams - Optional search parameters
 * @returns {string} The canonical URL
 */
export const getCanonicalUrl = (pathname = '', searchParams = {}) => {
  const canonicalDomain = getCanonicalDomain();

  // Clean pathname
  const cleanPath = pathname.startsWith('/') ? pathname : `/${pathname}`;

  // Add search parameters if provided
  const urlParams = new URLSearchParams(searchParams);
  const queryString = urlParams.toString();

  return `${canonicalDomain}${cleanPath}${queryString ? `?${queryString}` : ''}`;
};

/**
 * Get current canonical URL from window location
 * @returns {string} The canonical URL for current page
 */
export const getCurrentCanonicalUrl = () => {
  if (typeof window === 'undefined') {
    return getCanonicalDomain();
  }

  return getCanonicalUrl(window.location.pathname, window.location.search);
};

/**
 * Check if current domain is the canonical domain
 * @returns {boolean} True if on canonical domain
 */
export const isCanonicalDomain = () => {
  if (typeof window === 'undefined') {
    return true;
  }

  // Allow localhost and development domains
  const hostname = window.location.hostname;
  return (
    hostname === 'vinspectorai.com' ||
    hostname.includes('localhost') ||
    hostname.includes('127.0.0.1')
  );
};

/**
 * Redirect to canonical domain if needed (client-side)
 * Call this in useEffect for SPA pages
 */
export const redirectToCanonicalDomain = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const currentUrl = window.location.href;

  // Handle HTTP to HTTPS redirect (skip for localhost)
  if (
    currentUrl.startsWith('http://') &&
    !window.location.hostname.includes('localhost') &&
    !window.location.hostname.includes('127.0.0.1')
  ) {
    const httpsUrl = currentUrl.replace('http://', 'https://');
    console.log('[SEO] Redirecting HTTP to HTTPS:', httpsUrl);
    window.location.replace(httpsUrl);
    return;
  }

  // Handle www to non-www redirect
  if (window.location.hostname === 'www.vinspectorai.com') {
    const canonicalUrl = getCurrentCanonicalUrl();
    console.log('[SEO] Redirecting www to canonical:', canonicalUrl);
    window.location.replace(canonicalUrl);
    return;
  }

  // Handle any other non-canonical domains (skip for localhost)
  if (
    !isCanonicalDomain() &&
    !window.location.hostname.includes('localhost') &&
    !window.location.hostname.includes('127.0.0.1')
  ) {
    const canonicalUrl = getCurrentCanonicalUrl();
    console.log('[SEO] Redirecting to canonical domain:', canonicalUrl);
    window.location.replace(canonicalUrl);
  }
}; /**
 * Generate structured data for page
 * @param {Object} pageData - Page-specific data
 * @returns {Object} Structured data object
 */
export const generateStructuredData = (pageData = {}) => {
  const canonicalUrl = getCurrentCanonicalUrl();

  return {
    '@context': 'https://schema.org',
    '@type': pageData.type || 'WebPage',
    '@id': canonicalUrl,
    url: canonicalUrl,
    name: pageData.title || 'VinSpector AI',
    description: pageData.description || 'Vehicle History Reports & VIN Search',
    publisher: {
      '@type': 'Organization',
      name: 'VinSpector AI',
      url: getCanonicalDomain(),
      logo: {
        '@type': 'ImageObject',
        url: `${getCanonicalDomain()}/images/logo.png`,
      },
    },
    ...pageData.additionalData,
  };
};

/**
 * Get SEO metadata for any page
 * @param {Object} options - Page options
 * @returns {Object} Complete SEO metadata
 */
export const getSEOMetadata = (options = {}) => {
  const {
    title = 'VinSpector AI - Vehicle History Reports & VIN Search',
    description = 'Get comprehensive vehicle history reports with our VIN search tool. Make informed decisions when buying used cars.',
    pathname = '',
    searchParams = {},
    ogImage = `${getCanonicalDomain()}/images/logo-og.png`,
    type = 'website',
  } = options;

  const canonicalUrl = getCanonicalUrl(pathname, searchParams);
  const fullTitle = title.includes('VinSpector AI')
    ? title
    : `${title} | VinSpector AI`;

  return {
    title: fullTitle,
    description,
    canonical: canonicalUrl,
    ogTitle: fullTitle,
    ogDescription: description,
    ogUrl: canonicalUrl,
    ogImage,
    ogType: type,
    twitterCard: 'summary_large_image',
    twitterUrl: canonicalUrl,
    twitterTitle: fullTitle,
    twitterDescription: description,
    twitterImage: ogImage,
    structuredData: generateStructuredData({
      title: fullTitle,
      description,
      type: type === 'article' ? 'Article' : 'WebPage',
    }),
  };
};
